import { useEffect, useRef, useCallback } from "react";
import marketdata from "../data/Services/marketdata";
import { router as ordersRouter } from "../data/Services/ordersRouter";
import { useAuthStore } from "../store/authStore";
import logger from "../utils/logger";
import {
  isMarketdataHealthy as coreIsMarketdataHealthy,
  isOrdersRouterHealthy as coreIsOrdersRouterHealthy,
  reconnectMarketdata as coreReconnectMarketdata,
} from "./pageVisibilityReconnectCore";

/**
 * Hook that handles automatic reconnection when the page becomes visible after being hidden.
 *
 * This is critical for mobile browsers (especially Android Chrome) where:
 * - Background tabs are suspended to save battery
 * - SignalR WebSocket connections are terminated
 * - The app needs to reconnect when the user returns
 *
 * The hook monitors:
 * 1. Page Visibility API (visibilitychange event)
 * 2. SignalR connection states for both marketdata and ordersRouter
 * 3. Heartbeat staleness to detect "zombie" connections
 */
export const usePageVisibilityReconnect = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const lastVisibilityChange = useRef(Date.now());
  const reconnectInProgress = useRef(false);

  // Minimum time hidden before we check for reconnection (5 seconds)
  // This prevents unnecessary reconnection checks for quick tab switches
  const MIN_HIDDEN_TIME_MS = 5000;

  // Maximum heartbeat age before considering connection stale (60 seconds)
  // SignalR sends heartbeats every ~15 seconds, so 60s means 4 missed heartbeats
  const MAX_HEARTBEAT_AGE_MS = 60000;

  // On wake, a throttled tab makes lastHeartbeat look stale even when the socket survived.
  // The server sends app-level HBs (≤5s) + SignalR keepalive (15s); grant this grace for a
  // fresh HB to land before tearing down a still-'online' connection (full re-auth + re-handshake
  // + re-subscribe is far costlier than waiting one keepalive interval).
  const HB_GRACE_MS = 20000;

  /**
   * Check if marketdata connection is healthy
   */
  const isMarketdataHealthy = useCallback(
    () => coreIsMarketdataHealthy(marketdata),
    [],
  );

  const isOrdersRouterHealthy = useCallback(
    () => coreIsOrdersRouterHealthy(ordersRouter),
    [],
  );

  const reconnectMarketdata = useCallback(
    () => coreReconnectMarketdata(marketdata),
    [],
  );

  /**
   * Attempt to reconnect ordersRouter if disconnected
   */
  const reconnectOrdersRouter = useCallback(async () => {
    // Only reconnect if user was previously logged in
    if (ordersRouter.idle || !ordersRouter.broker || !ordersRouter.user) {
      return true; // Not expected to be connected
    }

    try {
      console.log(
        `[PageVisibility] 🔄 Reconnecting ordersRouter to ${ordersRouter.broker}...`,
      );

      logger.orders.info("Reconectando roteador após retorno da aba", {
        broker: ordersRouter.broker,
        user: ordersRouter.user,
      });

      await ordersRouter.login(
        ordersRouter.broker,
        ordersRouter.user,
        ordersRouter.pwd,
      );

      console.log("[PageVisibility] ✅ OrdersRouter reconnected successfully");
      return true;
    } catch (error) {
      console.error(
        "[PageVisibility] ❌ OrdersRouter reconnection failed:",
        error,
      );
      return false;
    }
  }, []);

  /**
   * Handle page visibility change
   */
  const handleVisibilityChange = useCallback(async () => {
    const isVisible = !document.hidden;
    const now = Date.now();
    const timeHidden = now - lastVisibilityChange.current;

    // Update last change time
    lastVisibilityChange.current = now;

    // Issue #317 diagnostic: ship every visibility transition with the data
    // needed to correlate window-focus events with positions freezing.
    // Volume is naturally low (handful per minute max).
    try {
      logger.diagnostic.info("[Visibility] change", {
        isVisible,
        timeHiddenMs: timeHidden,
        willEarlyReturnBriefHidden:
          isVisible && timeHidden < MIN_HIDDEN_TIME_MS,
        documentVisibilityState: document.visibilityState,
        hasFocus:
          typeof document.hasFocus === "function" ? document.hasFocus() : null,
      });
    } catch (e) {
      console.warn("[PageVisibility] log emit failed", e);
    }

    // Only act when page becomes visible
    if (!isVisible) {
      console.log("[PageVisibility] Page hidden");
      return;
    }

    console.log(
      `[PageVisibility] Page visible after ${Math.round(timeHidden / 1000)}s`,
    );

    // Skip if not authenticated
    if (!isAuthenticated) {
      console.log(
        "[PageVisibility] Not authenticated, skipping reconnection check",
      );
      return;
    }

    // Skip if hidden for less than threshold
    if (timeHidden < MIN_HIDDEN_TIME_MS) {
      console.log(
        "[PageVisibility] Hidden briefly, skipping reconnection check",
      );
      return;
    }

    // Prevent concurrent reconnection attempts
    if (reconnectInProgress.current) {
      console.log("[PageVisibility] Reconnection already in progress");
      return;
    }

    reconnectInProgress.current = true;

    try {
      // If the connection is still 'online' but the heartbeat merely aged during tab throttle,
      // give it a short grace for the next server HB to land before deciding to reconnect —
      // avoids tearing down a live socket that self-heals within a keepalive interval (C2 trigger).
      if (
        marketdata.state === "online" &&
        Date.now() - marketdata.lastHeartbeat > MAX_HEARTBEAT_AGE_MS
      ) {
        console.log(
          `[PageVisibility] online but HB stale on wake — waiting ${HB_GRACE_MS / 1000}s for a fresh HB before deciding`,
        );
        await new Promise((resolve) => setTimeout(resolve, HB_GRACE_MS));
      }

      // Check marketdata health
      const marketdataHealthy = isMarketdataHealthy();
      const ordersRouterHealthy = isOrdersRouterHealthy();

      console.log(
        `[PageVisibility] Health check - Marketdata: ${marketdataHealthy ? "✅" : "❌"}, OrdersRouter: ${ordersRouterHealthy ? "✅" : "❌"}`,
      );

      // Reconnect if needed
      const reconnectPromises = [];

      if (!marketdataHealthy) {
        reconnectPromises.push(reconnectMarketdata());
      }

      if (!ordersRouterHealthy) {
        reconnectPromises.push(reconnectOrdersRouter());
      }

      if (reconnectPromises.length > 0) {
        await Promise.all(reconnectPromises);
      } else {
        console.log("[PageVisibility] All connections healthy");
      }

      // Always refresh order data when page becomes visible (even if connections are healthy)
      // This syncs any updates that happened while the tab was throttled/background
      if (!ordersRouter.idle && ordersRouter.state === "online") {
        console.log(
          "[PageVisibility] 🔄 Refreshing order data after visibility change",
        );
        try {
          await ordersRouter.getAccountDataAsync();
        } catch (error) {
          console.warn("[PageVisibility] Failed to refresh order data:", error);
        }
      }
    } finally {
      reconnectInProgress.current = false;
    }
  }, [
    isAuthenticated,
    isMarketdataHealthy,
    isOrdersRouterHealthy,
    reconnectMarketdata,
    reconnectOrdersRouter,
  ]);

  // Set up visibility change listener
  useEffect(() => {
    // Initialize last visibility change time
    lastVisibilityChange.current = Date.now();

    // Add listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    console.log(
      "[PageVisibility] Hook initialized - listening for visibility changes",
    );

    // Cleanup
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      console.log(
        "[PageVisibility] Hook cleanup - removed visibility listener",
      );
    };
  }, [handleVisibilityChange]);

  // Also listen for window focus as a backup (some mobile browsers use this)
  useEffect(() => {
    const handleFocus = () => {
      // Issue #317 diagnostic: ship every focus event so we can correlate
      // with order/position state transitions. Volume bounded by user
      // alt-tab behavior.
      try {
        logger.diagnostic.info("[Visibility] window focus", {
          documentHidden: document.hidden,
          documentVisibilityState: document.visibilityState,
        });
      } catch (e) {
        console.warn("[PageVisibility] focus log failed", e);
      }
      // Small delay to let visibilitychange fire first if it's going to
      setTimeout(() => {
        if (!document.hidden) {
          handleVisibilityChange();
        }
      }, 100);
    };

    const handleBlur = () => {
      // Issue #317 diagnostic: blur is the trigger for Chrome's wake-up
      // throttling on the window's setTimeout callbacks. Ship it.
      try {
        logger.diagnostic.info("[Visibility] window blur", {
          documentHidden: document.hidden,
          documentVisibilityState: document.visibilityState,
        });
      } catch (e) {
        console.warn("[PageVisibility] blur log failed", e);
      }
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  }, [handleVisibilityChange]);
};

export default usePageVisibilityReconnect;
